// Presentation only: keep the original origin ID for matching and data access.
const SOURCES = {
  "com.google.android.apps.fitness": "Google Fit",
  "com.sec.android.app.shealth": "Samsung Health",
  "com.huawei.health": "Huawei Health",
  "com.huawei.healthglobal": "Huawei Health",
  "com.fitbit.FitbitMobile": "Fitbit",
  "com.garmin.android.apps.connectmobile": "Garmin Connect",
  "com.garmin.connect.mobile": "Garmin Connect",
  "nl.appyhapps.healthsync": "Health Sync",
  "com.xiaomi.wearable": "Mi Fitness",
  "com.xiaomi.hm.health": "Zepp Life",
  "com.huami.watch.hmwatchmanager": "Zepp",
  "com.heytap.health": "OHealth",
  "com.strava": "Strava",
  "com.polar.polarflow": "Polar Flow",
  "com.suunto.app": "Suunto",
  "com.ouraring.oura": "Oura",
  "ch.wellcomefit.roody": "Roody",
}

export function healthSourceLabel(source) {
  const id = typeof source === "string" ? source : source?.sourceBundleId || source?.source || ""
  const name = typeof source === "object" ? source?.sourceName : source
  if (SOURCES[id]) return SOURCES[id]
  if (SOURCES[name]) return SOURCES[name]
  const label = typeof name === "string" ? name.trim().slice(0, 80) : ""
  return label && !/^[a-z][\w-]*(?:\.[\w-]+){2,}$/i.test(label) ? label : "Verbundene Health-App"
}

const measurement = value => typeof value === "number" && Number.isFinite(value) && value >= 0

export function healthWorkoutEnergy(execution, recording) {
  if (measurement(recording?.activeEnergyKilocalories)) return {
    value: recording.activeEnergyKilocalories, kind: "active", label: "Aktive Kalorien", source: healthSourceLabel(recording), estimated: false,
  }
  if (measurement(recording?.totalEnergyKilocalories)) return {
    value: recording.totalEnergyKilocalories, kind: "total", label: "Gesamtkalorien", source: healthSourceLabel(recording), estimated: false,
  }
  const watch = execution?.watchData?.health?.activeEnergyKilocalories
  if (measurement(watch)) return { value: watch, kind: "active", label: "Aktive Kalorien", source: "Roody Watch", estimated: false }
  const raw = execution?.caloriesBurned
  const fallback = raw !== null && raw !== undefined && raw !== "" ? Number(raw) : NaN
  return { value: Number.isFinite(fallback) && fallback >= 0 ? fallback : null, kind: "estimate", label: "Kalorien · geschätzt", source: "Roody", estimated: true }
}

export function healthActivityIcon(workout) {
  // Android already sends ExerciseSessionRecord.exerciseType. A user-defined
  // title must not turn a swim or a bike ride into a generic training icon.
  // These numbers belong to Health Connect, not Apple/Huawei's enums.
  if (workout?.provider === "health_connect" && Number.isInteger(workout.activityType) && HEALTH_CONNECT_ICONS[workout.activityType]) {
    return HEALTH_CONNECT_ICONS[workout.activityType]
  }
  const type = `${workout?.activityFamily || ""} ${workout?.activityName || workout?.title || ""}`.toLowerCase()
  if (/strength|kraft|weight/.test(type)) return "mdi-dumbbell"
  if (/\brun\b|running|lauf|jogg/.test(type)) return "mdi-run"
  if (/walk|geh|wander/.test(type)) return "mdi-walk"
  if (/cycl|rad|bike|biking|velo|spinning/.test(type)) return "mdi-bike"
  if (/swim|schwimm|aqua/.test(type)) return "mdi-swim"
  if (/yoga/.test(type)) return "mdi-yoga"
  if (/pilates|stretch/.test(type)) return "mdi-human-handsup"
  if (/hiking/.test(type)) return "mdi-walk"
  if (/hyrox|mixed|hiit|cross/.test(type)) return "mdi-lightning-bolt-outline"
  if (/group_fitness/.test(type)) return "mdi-account-group-outline"
  return "mdi-motion-play-outline"
}

// AndroidX ExerciseSessionRecord constants:
// https://github.com/androidx/androidx/blob/androidx-main/health/connect/connect-client/src/main/java/androidx/health/connect/client/records/ExerciseSessionRecord.kt
const HEALTH_CONNECT_ICONS = Object.freeze({
  2: "mdi-badminton", 4: "mdi-baseball", 5: "mdi-basketball",
  8: "mdi-bike", 9: "mdi-bike", // biking, stationary biking
  10: "mdi-lightning-bolt-outline", 11: "mdi-boxing-glove", 13: "mdi-weight-lifter",
  14: "mdi-cricket", 16: "mdi-human-female-dance", 25: "mdi-run",
  26: "mdi-account-group-outline", 27: "mdi-fencing",
  28: "mdi-football", 29: "mdi-football-australian", 31: "mdi-disc", 32: "mdi-golf",
  33: "mdi-meditation", 34: "mdi-gymnastics", 35: "mdi-handball",
  36: "mdi-lightning-bolt-outline", 37: "mdi-hiking",
  38: "mdi-hockey-sticks", 39: "mdi-skate", 44: "mdi-karate",
  46: "mdi-kayaking", 47: "mdi-paragliding", 48: "mdi-human-handsup",
  50: "mdi-tennis", 51: "mdi-hiking", 52: "mdi-hockey-sticks",
  53: "mdi-rowing", 54: "mdi-rowing", 55: "mdi-rugby",
  56: "mdi-run", 57: "mdi-run", 58: "mdi-sail-boat", 59: "mdi-diving-scuba",
  60: "mdi-roller-skate", 61: "mdi-ski", 62: "mdi-snowboard", 63: "mdi-snowshoeing",
  64: "mdi-soccer", 65: "mdi-baseball", 66: "mdi-tennis", 68: "mdi-stairs-up", 69: "mdi-stairs-up",
  70: "mdi-dumbbell", 71: "mdi-human-handsup", 72: "mdi-surfing",
  73: "mdi-swim", 74: "mdi-swim", 75: "mdi-table-tennis", 76: "mdi-tennis",
  78: "mdi-volleyball", 79: "mdi-walk", 80: "mdi-water-polo", 81: "mdi-dumbbell",
  82: "mdi-wheelchair", 83: "mdi-yoga",
})
